import React from 'react';
import { Agent, Workflow } from '../../model';
import { Input, Badge } from '../../ui';
import { agentStatusConfigs } from '../../ui/Badge/statusConfigs';
import './AgentForm.scss';

export interface AgentFormProps {
  agent: Agent | null;
  workflows: Workflow[];
  workflowsLoading: boolean;
  showValidationErrors: boolean;
  onAgentChange: (agent: Agent) => void;
  onSave: () => void;
  onDelete?: () => void;
  isNewAgent: boolean;
  isSaveDisabled: boolean;
  isDeleteDisabled: boolean;
  loading: boolean;
  createWorkflow: boolean;
  onCreateWorkflowChange: (createWorkflow: boolean) => void;
}

const AgentForm: React.FC<AgentFormProps> = ({
  agent,
  workflows,
  workflowsLoading,
  showValidationErrors,
  onAgentChange,
  onSave,
  onDelete,
  isNewAgent,
  isSaveDisabled,
  isDeleteDisabled,
  loading,
  createWorkflow,
  onCreateWorkflowChange,
}) => {
  if (!agent) return null;

  const isBehaviourValid = agent.behaviour && agent.behaviour.trim().length > 0;
  const isWorkflowValid = createWorkflow || agent.workflowUuid;

  return (
    <div className="agent-form">
      <div className="agent-form__header">
        <h1>{isNewAgent ? 'Create New Agent' : 'Agent Details'}</h1>
        <Badge status={agent.status || ''} statusConfigs={agentStatusConfigs} />
        <div className="agent-form__actions">
          <button
            className="save-button"
            onClick={onSave}
            disabled={isSaveDisabled || loading}>
            {loading ? 'Saving...' : 'Save'}
          </button>
          {!isNewAgent && onDelete && (
            <button
              className="delete-button"
              onClick={onDelete}
              disabled={isDeleteDisabled || loading}>
              {loading ? 'Deleting...' : 'Delete'}
            </button>
          )}
        </div>
      </div>

      <div className="agent-form__content">
        <div className="form-group">
          <Input
            label="Name"
            value={agent.name}
            onChange={value => onAgentChange({ ...agent, name: value })}
            placeholder="Enter agent name"
            required
            fullWidth
          />
        </div>

        <div className="form-group">
          <Input
            label="Behaviour"
            value={agent.behaviour}
            onChange={value => onAgentChange({ ...agent, behaviour: value })}
            placeholder="Enter agent behaviour description"
            required
            fullWidth
            error={
              showValidationErrors && !isBehaviourValid
                ? 'Behaviour is required'
                : ''
            }
          />
        </div>

        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={createWorkflow}
              onChange={e => onCreateWorkflowChange(e.target.checked)}
              className="checkbox-field"
            />
            <span className="checkbox-text">Create workflow automatically</span>
          </label>
        </div>

        {!createWorkflow && (
          <div className="form-group">
            <label className="form-label">
              Workflow
              <select
                className={`select-field ${
                  !isWorkflowValid && showValidationErrors ? 'error' : ''
                }`}
                value={agent.workflowUuid || ''}
                onChange={e =>
                  onAgentChange({
                    ...agent,
                    workflowUuid: e.target.value || undefined,
                  })
                }
                disabled={workflowsLoading}>
                <option value="">Select a workflow</option>
                {workflows.map(workflow => (
                  <option key={workflow.uuid} value={workflow.uuid}>
                    {workflow.name}
                  </option>
                ))}
              </select>
            </label>
            {showValidationErrors && !isWorkflowValid && (
              <div className="error-message">Workflow is required</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentForm;
